import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Download, FileSpreadsheet, Trash2, Search, Mail, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const SubscribersManager = () => {
  const { toast } = useToast();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [csvBusy, setCsvBusy] = useState(false);
  const csvRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/newsletter-subscribers`, { headers: authHeader() });
      setSubs(r.data || []);
    } catch (e) {
      toast({ title: 'Failed to load subscribers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExport = () => {
    // Browser-native download — uses the existing CSV streaming endpoint
    const token = localStorage.getItem('admin_token');
    axios
      .get(`${API}/api/admin/newsletter-subscribers/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `paisley-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast({ title: 'Export failed', variant: 'destructive' }));
  };

  const handleCsv = async (file) => {
    if (!file) return;
    setCsvBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/api/admin/newsletter-subscribers/import-csv`, fd, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      const { created, reactivated, existing, invalid } = r.data;
      toast({
        title: 'Import complete',
        description: `${created} new · ${reactivated} reactivated · ${existing} already on list · ${invalid} invalid`,
      });
      load();
    } catch (e) {
      toast({ title: 'Import failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setCsvBusy(false);
      if (csvRef.current) csvRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const csv = 'email,name,source\nfriend@example.com,Friend Friendly,newspaper-flyer\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'paisley-subscribers-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const removeSubscriber = async (email) => {
    if (!window.confirm(`Remove ${email} from the list?`)) return;
    try {
      await axios.delete(`${API}/api/admin/newsletter-subscribers/${encodeURIComponent(email)}`, { headers: authHeader() });
      toast({ title: 'Subscriber removed' });
      load();
    } catch (_) {
      toast({ title: 'Remove failed', variant: 'destructive' });
    }
  };

  const filtered = query
    ? subs.filter((s) => (s.email || '').toLowerCase().includes(query.toLowerCase()) || (s.name || '').toLowerCase().includes(query.toLowerCase()))
    : subs;

  const fmtDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch (_) { return String(d); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard"><Button variant="outline" size="sm"><ArrowLeft size={16} className="mr-2" />Dashboard</Button></Link>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Mail size={22} className="text-sky-500" />Newsletter Subscribers</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button type="button" variant="outline" onClick={downloadTemplate} data-testid="subs-csv-template-btn">
              <Download size={14} className="mr-2" />CSV template
            </Button>
            <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={(e) => handleCsv(e.target.files?.[0])} />
            <Button type="button" variant="outline" disabled={csvBusy} onClick={() => csvRef.current?.click()} data-testid="subs-csv-import-btn">
              <FileSpreadsheet size={14} className="mr-2" />{csvBusy ? 'Importing…' : 'Import CSV'}
            </Button>
            <Button type="button" onClick={handleExport} className="bg-sky-600 hover:bg-sky-700" data-testid="subs-csv-export-btn">
              <Download size={14} className="mr-2" />Export CSV
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Active subscribers</p>
            <p className="text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
              <Users size={20} className="text-sky-500" />{loading ? '…' : subs.length}
            </p>
          </div>
        </div>

        {/* Search + list */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3">
            <Search size={15} className="text-gray-400" />
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by email or name…"
              className="border-0 shadow-none px-0 focus-visible:ring-0"
              data-testid="subs-search-input"
            />
            <span className="text-xs text-gray-500 shrink-0">{filtered.length} shown</span>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Mail size={32} className="mx-auto text-gray-300 mb-3" />
              {subs.length === 0 ? 'No subscribers yet. Import a CSV or wait for sign-ups.' : 'No subscribers match that search.'}
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="subs-table">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-left px-5 py-3 hidden sm:table-cell">Name</th>
                  <th className="text-left px-5 py-3 hidden md:table-cell">Source</th>
                  <th className="text-left px-5 py-3 hidden md:table-cell">Subscribed</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((s) => (
                  <tr key={s.email} className="hover:bg-slate-50" data-testid={`subs-row-${s.email}`}>
                    <td className="px-5 py-3 font-medium text-slate-900 break-all">{s.email}</td>
                    <td className="px-5 py-3 text-slate-600 hidden sm:table-cell">{s.name || '—'}</td>
                    <td className="px-5 py-3 text-slate-500 hidden md:table-cell">
                      <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-xs">{s.source || 'web'}</span>
                    </td>
                    <td className="px-5 py-3 text-slate-500 hidden md:table-cell">{fmtDate(s.subscribed_at)}</td>
                    <td className="px-2 py-3">
                      <button
                        onClick={() => removeSubscriber(s.email)}
                        className="p-2 text-gray-400 hover:text-red-600"
                        title="Remove subscriber"
                        data-testid={`subs-delete-${s.email}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <p className="text-xs text-gray-500 mt-4">
          CSV import format: <code className="bg-slate-100 px-1.5 py-0.5 rounded">email,name,source</code>. Email is required; duplicates are skipped; previously-unsubscribed emails are reactivated.
        </p>
      </div>
    </div>
  );
};

export default SubscribersManager;
